import { Component, computed, OnInit } from '@angular/core';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { GridOptions } from 'src/app/shared/classes/list/grid-options';
import { GridColumnType } from 'src/models/ts/grid-column-type.model';
import { GridColumnDisplayPropertiesDto } from 'src/models/ts/grid-column-display-properties-dto.model';
import { GridContextPermissionsMetadata } from '../../../../widgets/collection-list-widget/interfaces/grid-context-permissions-metadata';
import { ListDesignGridOptionsDto } from 'src/models/ts/list-design-grid-options-dto.model';
import { DataInstancePermissionsDto } from 'src/models/ts/data-instance-permissions-dto';
import { FormControlComponent } from '../form-control/form-control.component';
import { FormFieldGridCellComponent } from '../form-list-control/components/form-field-grid-cell/form-field-grid-cell.component';
import { GridCellComponent } from 'src/app/shared/components/grid/cells/grid-cell/grid-cell.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { JsonPipe } from '@angular/common';
import { IconComponent } from 'src/app/shared/components/ui/icon/icon.component';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { AlphaNumericControlComponent } from '../alpha-numeric-control/alpha-numeric-control.component';
import { ChecklistEditCellComponent } from './components/checklist-edit-cell/checklist-edit-cell.component';
import { BaseFormControlComponent } from '../../../classes/base-form-control.component';
import {
  selectFormField,
  selectFormFieldsByProtectedFieldType,
  selectFormLockState
} from 'src/app/store/features/forms/forms-selectors';
import { ProtectedFieldType } from 'src/models/ts/protected-field-type.model';
import { CollectionFormField } from 'src/models/ts/collection-form-field.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChkOutcomeType, ResultClassValue } from 'src/models/ts/result-class-value';
import { ChecklistAnswerClassType } from 'src/models/ts/checklist-answer-class-type';
import { GridColumnBase } from 'src/app/shared/classes/list/grid-column-base';
import { UpdateDeleteState } from 'src/models/ts/update-delete-state.model';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { formsActions } from 'src/app/store/features/forms/forms-actions';
import { SearchComponent } from 'src/app/shared/components/ui/search/search.component';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { CellActionType } from 'src/app/features/bizzmine/widgets/collection-list-widget/classes/cell-action-type';
import { CellActionData } from 'src/app/features/bizzmine/widgets/collection-list-widget/interfaces/cell-action-data';
import { RangeType } from 'src/models/ts/range-type.model';
import { ColorService } from 'src/app/core/services/colors/color.service';
import { CHECKLIST_BUTTON_DEFAULT_COLORS } from 'src/app/features/bizzmine/form/constants/checklist-button-default-colors';


export const FROM_FIELD = 'FromValue';
export const TO_FIELD = 'ToValue';
export const OUTCOME_FIELD = 'Outcome';
export const SCORE_FIELD = 'Score';
export const BACKGROUNDCOLOR_FIELD = 'BackgroundColor';
export const FOREGROUNDCOLOR_FIELD = 'ForegroundColor';
export const ATTACHMENT_REQUIRED_FIELD = 'AttachmentRequired';
export const REMARKS_REQUIRED_FIELD = 'RemarksRequired';
export const VALUE_FIELD = 'Value';


@Component({
  selector: 'bizz-result-lists-checklist-control',
  standalone: true,
  imports: [
    GridComponent,
    FormControlComponent,
    FormFieldGridCellComponent,
    GridCellComponent,
    ReactiveFormsModule,
    FormsModule,
    JsonPipe,
    IconComponent,
    TooltipDirective,
    TranslatePipe,
    AlphaNumericControlComponent,
    ChecklistEditCellComponent,
    SearchComponent
  ],
  templateUrl: './result-lists-checklist-control.component.html',
  styleUrl: './result-lists-checklist-control.component.scss'
})
export class ResultListsChecklistControlComponent extends BaseFormControlComponent implements OnInit {
  public currentPage = 1;
  public gridOptions: GridOptions = {} as GridOptions;
  public loading = false;
  public totalItems = 0;
  public gridData: Array<any> = [];
  public filteredGridData: Array<any> = [];
  public gridCellMetadata: GridContextPermissionsMetadata = {} as GridContextPermissionsMetadata;
  public formGroup: FormGroup;
  public type: ChecklistAnswerClassType;
  public readonly ChecklistAnswerClassType = ChecklistAnswerClassType;
  public searchTerm: string = '';
  public searchTerm$ = new BehaviorSubject<string>(this.searchTerm);
  public ChkMainCollectionsID: number;
  public ChkResultClassID: number;
  private WIDTH = 200;

  public isReadOnly = computed(() => {
    const result = this.formFieldSignal()?.IsReadOnly == true || this.store$.selectSignal(selectFormLockState(this.formId))();
    this.gridCellMetadata.listOptions.Permissions.CanEditProperties = !result;
    return result;
  });

  public constructor(private fb: FormBuilder, private colorService: ColorService) {
    super();
    this.gridCellMetadata.listOptions = {} as ListDesignGridOptionsDto;
    this.gridCellMetadata.trashFilterActive = false;
    this.gridCellMetadata.listOptions.HasValidLicenseToEditCollection = true;
    this.gridCellMetadata.listOptions.Permissions = {} as DataInstancePermissionsDto;

    this.gridOptions = new GridOptions({
      name: 'Result Values',
      columns: [],
      scrollable: true,
      sortable: {
        allowUnsort: false,
        showIndexes: false,
        mode: 'multiple'
      },
      selectable: {
        enabled: false,
        checkboxOnly: true,
        mode: 'single',
        cell: false,
        drag: false
      },
      resizable: true,
      reorderable: true,
      dataSource: {
        group: [],
        sort: []
      },
      pageable: {
        info: true,
        previousNext: true,
        pageSizes: true,
        alwaysVisible: true,
        numeric: true,
        refresh: true
      },
      groupable: {
        messages: {
          empty: ''
        }
      }
    });

    this.gridOptions.groupable.enabled = false;
  }

  /**
   * Returns the grid data that is not marked for deletion
   * @returns {Array<any>}
   */
  public get activeGridData(): Array<any> {
    if (this.gridData == undefined)
      return [];
    return this.gridData.filter(row => row.UpdateDeleteState != UpdateDeleteState.Delete);
  }

  public filterGridData(): Array<any> {
    this.loading = true;
    let result = this.activeGridData;
    if (this.searchTerm != undefined && this.searchTerm.trim() != '') {
      result = result.filter(row => {
          let found = false;
          this.gridOptions.columns.forEach(col => {
            const value = row[col.field];
            if (value != undefined) {
              found = found || value?.toString()?.toLowerCase()?.includes(this.searchTerm.toLowerCase());
            }
          });
          return found;
        }
      );
    }
    this.loading = false;
    this.filteredGridData = result;
    this.totalItems = this.filteredGridData?.length ?? 0;
    return result;
  }

  public get pagedAndFiltered(): Array<any> {
    return this.filteredGridData.slice((this.currentPage - 1) * this.gridOptions.pageSize, this.currentPage * this.gridOptions.pageSize);
  }

  public searchTermChanged(value: string): void {
    this.searchTerm = value;
    this.filterGridData();
  }

  public pageChanged(event: PageChangeEvent): void {
    this.currentPage = (event.skip / event.take) + 1;
    this.gridOptions.pageSize = event.take;
  }

  public override ngOnInit(): void {
    const fieldId = this.formFieldSignal()?.Id ?? 0;
    const field = this.store$.selectSignal(selectFormField(this.formId, fieldId))();

    this.formField$.pipe(takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged(this.fieldValueChangeComparator),
      tap((field) => {
        if (field) {
          const values = (field?.Value?.Values ?? []) as Array<ResultClassValue>;
          this.ChkMainCollectionsID = structuredClone(field?.Value?.ChkMainCollectionsID);
          this.ChkResultClassID = structuredClone(field?.Value?.ChkResultClassID);
          this.gridData = this.mapValuesToGrid(values);
          this.initGridColumns(this.type);
          this.filterGridData();
        }
      })).subscribe();


    this.store$.select(selectFormFieldsByProtectedFieldType(this.formId, field!.CollectionsID, [ProtectedFieldType.ChkResultClassType]))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fields => {
        if (fields) {
          const chkResultClassType = fields[ProtectedFieldType.ChkResultClassType] as CollectionFormField;
          const fieldValues = chkResultClassType.FieldValues;
          const value = chkResultClassType?.Value;
          if (value != undefined && fieldValues != undefined) {
            const type = fieldValues?.find(x => x.CollectionFieldValuesID === value);
            if (type) {
              const id = +type.ProtectedFieldValueType;
              this.setType(id as ChecklistAnswerClassType);
            }
          }
        }
      });
  }

  private defaultColor: { BackgroundColor: string; ForegroundColor: string; } | null;

  public addRecord(): void {
    const newRecords = this.gridData?.filter(_ => _.ID < 0);
    let newRecordId: number;
    if (newRecords != undefined && newRecords.length > 0) {
      const lowestRecord = newRecords.reduce((prev, current) => (prev.ID < current.ID) ? prev : current);
      newRecordId = lowestRecord.ID - 1;
    } else {
      newRecordId = -1;
    }
    const newRow = {
      ID: newRecordId,
      UpdateDeleteState: UpdateDeleteState.Update,
      EditMode: true
    } as ResultClassValue;

    if (this.defaultColor != null) {
      newRow.BackgroundColor = this.defaultColor.BackgroundColor;
      newRow.ForegroundColor = this.defaultColor.ForegroundColor;
    }

    this.gridData.push(newRow);
    const rowIndex = this.activeGridData.findIndex(row => row == newRow);
    if (rowIndex > 0) //set the from value of the newRow by prentending we updated the previous row's to field
      this.onEditCellValueUpdated(this.activeGridData[rowIndex - 1], TO_FIELD);

    this.formGroup.addControl(newRow.ID.toString(), this.loadDataRowFormGroup(newRow));
    this.filterGridData();
    this.updateFormFieldValue();
  }

  public validateData(dataRow: any): boolean {
    const formGroup = this.formGroup.get(dataRow.ID.toString()) as FormGroup;
    return formGroup.valid;
  }

  public changeEditMode(dataRow: any, editMode: boolean): void {
    dataRow.EditMode = editMode;
    //We can only disable edit mode (sortof save)  if the data is valid
    if (!dataRow.EditMode && !this.validateData(dataRow)) {
      dataRow.EditMode = true;
    }
  }

  public deleteRecord(dataRow: any): void {
    dataRow.UpdateDeleteState = UpdateDeleteState.Delete;
    this.updateFormFieldValue();
  }

  public mapValuesToGrid(values: Array<ResultClassValue>): Array<any> {
    return structuredClone(values).map(el => {
      return { ...el, EditMode: el.EditMode ?? false };
    });
  }

  public onEditCellValueUpdated(data: any, field: string): void {
    if (this.type == ChecklistAnswerClassType.Numeric ||
      this.type == ChecklistAnswerClassType.SingleChoice) {
      switch (field) {
        case FROM_FIELD:
          this.onFromValueChanged(data);
          break;
        case TO_FIELD:
          this.onToValueChanged(data);
          break;
      }
    }

    this.updateFormFieldValue();
  }

  private onFromValueChanged(rowData: any): void {
    const rowIndex = this.activeGridData.findIndex(r => r.ID == rowData.ID);
    if (rowIndex > 0 && rowIndex <= this.activeGridData.length - 1) {
      this.activeGridData[rowIndex - 1][TO_FIELD] = rowData[FROM_FIELD];
      this.activeGridData[rowIndex - 1].State = UpdateDeleteState.Update;
    }
  }

  private onToValueChanged(rowData: any): void {
    const rowIndex = this.activeGridData.findIndex(r => r.ID == rowData.ID);
    if (rowIndex >= 0 && rowIndex < this.activeGridData.length - 1) {
      this.activeGridData[rowIndex + 1][FROM_FIELD] = rowData[TO_FIELD];
      this.activeGridData[rowIndex + 1].State = UpdateDeleteState.Update;
    }
  }

  private updateFormFieldValue(): void {
    const value = this.updateRangeTypes(this.gridData.map(el => el as ResultClassValue))?.filter(data => data.ID > 0 || (data.ID <= 0 && data.UpdateDeleteState != UpdateDeleteState.Delete));
    this.store$.dispatch(formsActions.updateFormFieldValue({
      formId: this.formId,
      value: {
        ChkMainCollectionsID: this.ChkMainCollectionsID,
        ChkResultClassID: this.ChkResultClassID,
        //Workaround because editCellValueUpdated is triggered twice for color and custom color
        //Sending the same data twice to the store makes your griddata readonly causing issues with the component..
        Values: structuredClone(value)
      },
      fieldId: this.formFieldSignal()!.Id
    }));
  }

  /**
   * Updates the numeric values and sets the rangetype depending on FromValue and ToValue
   * @param data
   * @returns ResultClassValue[]
   */
  private updateRangeTypes(data: ResultClassValue[]): ResultClassValue[] {
    data.forEach((result, index) => {
      if (result.FromValue == null && result.ToValue != null && index == 0) {
        result.RangeType = RangeType.FromNegativeInfinite;
      } else if (result.FromValue != null && result.ToValue == null && index == data.length - 1) {
        result.RangeType = RangeType.ToPositiveInfinite;
      } else
        result.RangeType = RangeType.FromToValue;
    });
    return data;
  }

  public onCellAction(action: { action: CellActionType, data: CellActionData }): void {
    if (action.action == CellActionType.CUSTOM_COLOR_CHANGE) {
      const data = this.gridData.find(_ => _.ID == action.data.data.data.ID);
      if (data) {
        const column = action.data.data.column;
        data[column.field] = action.data.data.value;
        data.UpdateDeleteState = UpdateDeleteState.Update;
        this.updateFormFieldValue();
      }
    }
  }

  public setType(type: ChecklistAnswerClassType): void {
    if (this.type != undefined && this.type != type) {
      this.gridData = this.gridData.filter(_ => _.ID > 0);
      this.gridData.forEach(el => el.UpdateDeleteState = UpdateDeleteState.Delete);
      this.updateFormFieldValue();
    }
    if (this.type == undefined || this.type != type) {
      this.initGridColumns(type);
    }
    this.type = type;
  }

  protected override focus(): void {
  }

  protected override fieldValueChangeComparator(previous: CollectionFormField | undefined, current: CollectionFormField | undefined): boolean {
    return JSON.stringify(previous?.Value) === JSON.stringify(current?.Value);
  }

  private initGridColumns(type: ChecklistAnswerClassType): void {
    this.loading = true;
    this.gridOptions.columns = [];
    if (type == ChecklistAnswerClassType.SingleChoice) {
      this.gridOptions.columns = (this.singleChoiceColumns() as Array<unknown> as Array<GridColumnBase>);
    }
    if (type == ChecklistAnswerClassType.Numeric) {
      this.gridOptions.columns = (this.numericColumns() as Array<unknown> as Array<GridColumnBase>);
    }
    this.gridOptions.columns = this.gridOptions.columns.concat((this.staticColumns(type) as Array<unknown> as Array<GridColumnBase>));

    this.formGroup = this.initForm();
    this.loading = false;
  }

  private staticColumns(type: ChecklistAnswerClassType): Array<GridColumnDisplayPropertiesDto> {

    const scores: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    scores.field = SCORE_FIELD;
    scores.GridColumnType = GridColumnType.Numeric;
    scores.title = 'Score (%)';
    scores.width = this.WIDTH;
    scores.required = true;

    const positiveNegativeType: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    positiveNegativeType.field = OUTCOME_FIELD;
    positiveNegativeType.GridColumnType = GridColumnType.Enum;
    positiveNegativeType.title = 'Positive/Negative';
    positiveNegativeType.width = this.WIDTH;
    positiveNegativeType.Enum = [
      {
        Text: 'Positive',
        Value: ChkOutcomeType.Positive
      },
      {
        Text: 'Negative',
        Value: ChkOutcomeType.Negative
      }];
    positiveNegativeType.required = true;


    const color: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    color.field = BACKGROUNDCOLOR_FIELD;
    color.GridColumnType = GridColumnType.ColorEnum;
    color.title = 'Color';
    color.width = this.WIDTH;

    this.defaultColor = CHECKLIST_BUTTON_DEFAULT_COLORS.find(col => col.Text == 'Green')?.Value ?? null;

    const customColor: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    customColor.field = BACKGROUNDCOLOR_FIELD;
    customColor.GridColumnType = GridColumnType.CustomColorEnum;
    customColor.title = 'Custom Color';
    customColor.width = this.WIDTH;
    customColor.hidden = !this.hasCustomColors();


    const attachmentReq: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    attachmentReq.field = ATTACHMENT_REQUIRED_FIELD;
    attachmentReq.GridColumnType = GridColumnType.Checkbox;
    attachmentReq.title = 'Attachment Required';
    attachmentReq.width = this.WIDTH;

    const remarksReq: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    remarksReq.field = REMARKS_REQUIRED_FIELD;
    remarksReq.GridColumnType = GridColumnType.Checkbox;
    remarksReq.title = 'Remarks Required';
    remarksReq.width = this.WIDTH;
    return [scores, positiveNegativeType, color, customColor, attachmentReq, remarksReq];
  }

  private hasCustomColors(): boolean {
    if (this.activeGridData == undefined || this.activeGridData.length == 0) {
      return false;
    }
    const currentValues = this.activeGridData.filter(row => row[BACKGROUNDCOLOR_FIELD] != undefined).map(row => row[BACKGROUNDCOLOR_FIELD]);
    //if a color is not found in the presetcolors it's custom.
    return currentValues.some(value => this.isCustomColor(value as string));
  }

  private isCustomColor(hexColor: string) {
    const defaultPresetsColors = CHECKLIST_BUTTON_DEFAULT_COLORS.filter(col => col.Text != 'Custom').map(col => col.Value?.BackgroundColor);
    return !defaultPresetsColors.some(presetColor => presetColor == hexColor);
  }

  private singleChoiceColumns(): Array<GridColumnDisplayPropertiesDto> {
    const values: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    values.field = VALUE_FIELD;
    values.GridColumnType = GridColumnType.Text;
    values.title = 'Values';
    values.width = this.WIDTH;
    values.required = true;

    return [values];
  }

  private numericColumns(): Array<GridColumnDisplayPropertiesDto> {
    const valuesFrom: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    valuesFrom.field = FROM_FIELD;
    valuesFrom.GridColumnType = GridColumnType.Numeric;
    valuesFrom.title = 'Value From';
    valuesFrom.width = this.WIDTH;

    const valuesTo: GridColumnDisplayPropertiesDto = {} as GridColumnDisplayPropertiesDto;
    valuesTo.field = TO_FIELD;
    valuesTo.GridColumnType = GridColumnType.Numeric;
    valuesTo.title = 'Value To';
    valuesTo.width = this.WIDTH;

    return [valuesFrom, valuesTo];
  }

  private initForm(): FormGroup {
    const form = this.fb.group({});
    this.gridData.forEach(el => {
      form.addControl(el.ID.toString(), this.loadDataRowFormGroup(el));
    });

    return form;
  }

  private loadDataRowFormGroup(data: any): FormGroup {
    const formGroup = this.fb.group({});
    this.gridOptions.columns.forEach(col => {
      const validators: Array<ValidatorFn> = [];
      if (col.required == true) {
        validators.push(Validators.required);
      }
      formGroup.addControl(col.field, new FormControl(data[col.field],
        {
          validators,
          updateOn: col.field == BACKGROUNDCOLOR_FIELD ? undefined : 'blur' //color fields should update instantly
        }));
    });

    return formGroup;
  }
}
